import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useGlobalPlayer } from '@/context/GlobalPlayerContext';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import LoadingSpinner from '@/components/LoadingSpinner';
import { isFreeMusic } from '@/utils/freeMusic';
import { Button } from '@/components/ui/button';
import { WifiOff } from 'lucide-react';

const SmartRedirect = () => {
  const { user } = useAuth();
  const { currentSong, isPlaying, isTransitioning, isSwitchingSong } = useGlobalPlayer();
  const navigate = useNavigate();
  const location = useLocation();
  const isOnline = useNetworkStatus();
  const hasRedirectedRef = useRef(false);
  const [showOfflinePrompt, setShowOfflinePrompt] = useState(false);

  // Detecta quando não há internet e mostra prompt para página offline
  useEffect(() => {
    if (!isOnline) {
      const offlineTimeout = setTimeout(() => {
        setShowOfflinePrompt(true);
      }, 3000); // Aguarda 3 segundos antes de mostrar o prompt offline

      return () => clearTimeout(offlineTimeout);
    } else {
      setShowOfflinePrompt(false);
    }
  }, [isOnline]);

  useEffect(() => {
    // Se não há internet e já passou o tempo, não faz redirecionamento normal
    if (!isOnline && showOfflinePrompt) {
      return;
    }

    // 🔧 CORREÇÃO 6: Não bloquear redirecionamento por causa do player
    // Apenas aguarda se está em processo de transição ativa
    if (isTransitioning || isSwitchingSong) {
      return;
    }

    if (hasRedirectedRef.current) {
      return;
    }

    const redirectTimeout = setTimeout(() => {
      if (hasRedirectedRef.current) return;

      try {
        const urlParams = new URLSearchParams(location.search);
        const musicId = urlParams.get('id');
        
        if (musicId && /^\d+$/.test(musicId)) {
          const songId = Number(musicId);
          
          if (isFreeMusic(songId)) {
            hasRedirectedRef.current = true;
            navigate(`/player?id=${musicId}`, { replace: true });
            return;
          }
          
          if (user) {
            hasRedirectedRef.current = true;
            navigate(`/player?id=${musicId}`, { replace: true });
            return;
          } else {
            hasRedirectedRef.current = true;
            navigate(`/auth?redirect=${encodeURIComponent(`/player?id=${musicId}`)}`, { replace: true });
            return;
          }
        }
        
        // 🔧 Se não há ID de música, redireciona baseado no usuário
        if (user) {
          hasRedirectedRef.current = true;
          navigate('/home', { replace: true });
        } else {
          hasRedirectedRef.current = true;
          navigate('/auth', { replace: true });
        }
      } catch (error) {
        if (!hasRedirectedRef.current) {
          hasRedirectedRef.current = true;
          navigate('/auth', { replace: true });
        }
      }
    }, 100);

    return () => {
      clearTimeout(redirectTimeout);
    };
  }, [user, navigate, location.search, isTransitioning, isSwitchingSong, isOnline, showOfflinePrompt]); // 🔧 Removidas dependências do player que podem bloquear

  useEffect(() => {
    hasRedirectedRef.current = false;
  }, [location.pathname]);

  // Se não há internet e passou o tempo, mostra prompt para página offline
  if (!isOnline && showOfflinePrompt) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-background p-4">
        <div className="text-center max-w-md">
          <div className="mb-6">
            <WifiOff className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-foreground mb-2">Sem Conexão</h2>
            <p className="text-muted-foreground">
              Você está offline. Verifique sua conexão com a internet e tente novamente.
            </p>
          </div>
          
          <Button
            variant="outline"
            onClick={() => window.location.reload()}
            className="w-full border-border text-foreground hover:bg-muted"
          >
            Tentar Novamente
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex items-center justify-center">
      <LoadingSpinner />
    </div>
  );
};

export default SmartRedirect; 